import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMantineColorScheme } from '@mantine/core';
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput.js';
import MyUserContext from '../myUserContext.js';
import ArtEventDropdown from './ArtEventDropdown'; // Import ArtEventDropdown component
import LogbookDropdown from './LogbookDropdown';

const CONSUMPTION_TAGS = new Set(["like", "veryGood", "good", "finished", "bad", "noRating"]);
const GLOBAL_MANUAL_TAGS = ["like", "veryGood", "good", "finished", "bad", "noRating", "inProgress", "todo"];

// Pill background: opaque Mantine default in BOTH schemes. The
// previous light-mode value used a transparent overlay, so the pill
// composited differently against a plain page body vs a tinted
// Paper — the same component looked different on /arts/{id} vs
// /users/:id/likes. Opaque = same color everywhere.
const pillBg = () => 'var(--mantine-color-default)';

const CardEventArtChatMini = ({ art, onFirstTagAdded, readOnly, libraryLinkText, fullWidth = false }) => {
  const myUser = useContext(MyUserContext);
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  // For my event. Hydrate from sessionStorage so a back-nav to the same
  // art flips straight to the 'tagged' pill state instead of flashing
  // the full rating ladder for one frame and then collapsing — which
  // bumps the whole page downward and shifts the restored scroll.
  const [artEvent, setArtEvent] = useState(() => {
    if (!art.myEventId) return null;
    try {
      const cached = sessionStorage.getItem(`art-myevent:${art.myEventId}`);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [myEvent, setMyEvent] = useState(() => artEvent?.event || null);
  useEffect(() => {
    if (!art.myEventId) {
      setArtEvent(null);
      setMyEvent(null);
      return;
    }
    // Re-hydrate on myEventId change too (ArtPage reuses this component
    // across /arts/A -> /arts/B; same tag-flash issue otherwise).
    try {
      const cached = sessionStorage.getItem(`art-myevent:${art.myEventId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        setArtEvent(parsed);
        setMyEvent(parsed?.event || null);
      }
    } catch {}
    axiosInstance
      .get(`/artEvents/${art.myEventId}`)
      .then((response) => {
        setArtEvent(response.data);
        setMyEvent(response.data.event);
        try { sessionStorage.setItem(`art-myevent:${art.myEventId}`, JSON.stringify(response.data)); } catch {}
      })
      .catch((error) => {
        console.error(error);
      });
  }, [art.myEventId]);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  const [isArtEventDropdownOpen, setIsArtEventDropdownOpen] = useState(false); // State for dropdown visibility
  // Inline date prompt: appears right after the user picks a
  // consumption tag so they can log "when" in one tap, without
  // navigating to the full /artEvents page.
  const [datePromptOpen, setDatePromptOpen] = useState(false);
  const [datePromptPosting, setDatePromptPosting] = useState(false);
  const [logbookDropdownOpen, setLogbookDropdownOpen] = useState(false);

  // Bg-tint the pill when the user hovers the interactive bits
  // (library link OR tags row), not when the cursor is over the
  // empty padding. No cursor-pointer on the pill itself — hover
  // is a visual affordance, not a click target.
  const pillRef = useRef(null);
  const setPillHover = (on) => {
    const el = pillRef.current;
    if (!el) return;
    el.style.background = on
      ? 'var(--mantine-color-default-hover)'
      : pillBg(colorScheme);
  };

  return (
    <div className={fullWidth ? "w-full" : "mx-auto max-w-xl"}>

      {/* Row: [prompt?] [tags] [chat icon] — visually a single pill like the
          paperplane / calendar rows on ArtPage. Tags stay inline-editable
          in both the "no library entry yet" and "already in library" states;
          only the chat icon opens the discussion overlay. The pill itself
          is no longer clickable and no longer highlights on hover — only
          the chat-icon button does. Prompt shows inside the pill when the
          art isn't yet in the user's library. */}
      <div className="flex justify-center">
      <div
        ref={pillRef}
        className={myEvent
          ? "flex flex-col items-center gap-2 px-3 pt-2 pb-3 rounded-2xl transition-colors"
          : "flex flex-col items-center gap-1"}
        style={myEvent
          ? { background: pillBg(colorScheme), minHeight: '48px', width: 'max-content', maxWidth: '100%' }
          : undefined}
        onMouseEnter={myEvent ? () => setPillHover(true) : undefined}
        onMouseLeave={myEvent ? () => setPillHover(false) : undefined}
      >
        {!art.myEventId && !myEvent && (() => {
          const noun = art.category === 1
            ? (language === "ru" ? "фильм" : "movie")
            : art.category === 2
              ? (language === "ru" ? "игру" : "game")
              : art.category === 3
                ? (language === "ru" ? "книгу" : "book")
                : (language === "ru" ? "объект" : "item");
          const text = language === "ru"
            ? `Выберите тэг, чтобы добавить ${noun} в свою библиотеку`
            : `Select tag to add the ${noun} to your library`;
          return (
            <span className="text-sm text-center" style={{ color: 'var(--mantine-color-dimmed)' }}>{text}</span>
          );
        })()}
        {myEvent && (
          <Link
            to={`/artEvents/${myEvent.id}`}
            className="text-sm underline-offset-2 hover:underline whitespace-nowrap px-4"
            style={{ color: 'var(--mantine-color-blue-text)' }}
          >
            {libraryLinkText || (language === 'ru' ? 'Перейти в мою библиотеку' : 'Go to my library')}
          </Link>
        )}
        <div className="flex items-center gap-2">
          <TagsInput
            key={myEvent?.id ?? 'new'}
            language={language}
            editable={!readOnly}
            flush
            artCategory={art.category}
            tags={myEvent?.eventTags || []}
            onSetGlobalManualTag={(tag) => {
              const landedOnConsumption = CONSUMPTION_TAGS.has(tag.name);
              if (myEvent === null) {
                // First tag ever — event doesn't exist yet. POST /artEvents
                // still takes the name (not id) for now.
                return axiosInstance
                  .post(`/artEvents`, { globalManualTag: tag.name, artId: art.id, type: "like" /*todo: remove once backend is ready, type is legacy*/ })
                  .then((response) => {
                    setMyEvent(response.data.event);
                    if (onFirstTagAdded) onFirstTagAdded(tag.name);
                    if (landedOnConsumption) setDatePromptOpen(true);
                  })
                  .catch(console.error);
              }
              const prevGmt = (myEvent.eventTags || []).find(t => GLOBAL_MANUAL_TAGS.includes(t.name));
              const prevWasConsumption = prevGmt && CONSUMPTION_TAGS.has(prevGmt.name);
              // Fire the hint callback when the user transitions INTO
              // consumption from a non-consumption state (todo/inProgress).
              const consumedNow = prevGmt && !CONSUMPTION_TAGS.has(prevGmt.name) && landedOnConsumption;
              return axiosInstance
                .post(`/artEvents/tag/${myEvent.id}/globalManualTag`, { tagId: tag.id })
                .then((response) => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: [
                      ...(prev.eventTags || []).filter(t => !GLOBAL_MANUAL_TAGS.includes(t.name)),
                      response.data,
                    ],
                  }) : prev);
                  if (consumedNow && onFirstTagAdded) onFirstTagAdded(tag.name);
                  // Show the inline date prompt whenever the user just
                  // landed on a consumption tag from a non-consumption
                  // state. Picking a different consumption tag
                  // (like → finished) doesn't re-prompt.
                  if (landedOnConsumption && !prevWasConsumption) setDatePromptOpen(true);
                })
                .catch(console.error);
            }}
            onAddCustomTag={(name) => {
              if (!myEvent) return;
              return axiosInstance
                .post(`/artEvents/tag/${myEvent.id}/customTag`, { name })
                .then((response) => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: [...(prev.eventTags || []), response.data],
                  }) : prev);
                })
                .catch(console.error);
            }}
            onRemoveCustomTag={(tagId) => {
              if (!myEvent) return;
              return axiosInstance
                .delete(`/artEvents/tag/${myEvent.id}/customTag`, { data: { tagId } })
                .then(() => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: (prev.eventTags || []).filter(t => t.id !== tagId),
                  }) : prev);
                })
                .catch(console.error);
            }}
          />
          {/* The chat_icon / "Go to discussion" button used to live here.
              Replaced by the library link rendered above the tags row.
              (Keeping the surrounding row so tags stay in the same
              horizontal container as before for spacing.) */}
        </div>
        {/* Inline date prompt. Appears right after a consumption-tag
            click; one tap on Сейчас / Вчера posts the date, "Другая
            дата" falls back to the full LogbookDropdown. Keeps the
            tag-then-date flow on the art page in one place. */}
        {datePromptOpen && myEvent && (
          <QuickDatePrompt
            artId={art.id}
            language={language}
            posting={datePromptPosting}
            setPosting={setDatePromptPosting}
            onOpenFull={() => { setLogbookDropdownOpen(true); setDatePromptOpen(false); }}
            onPosted={() => setDatePromptOpen(false)}
            isMovie={art.category === 1}
            category={art.category}
          />
        )}
      </div>
      </div>
      {logbookDropdownOpen && myEvent && (
        <LogbookDropdown
          artId={art.id}
          userId={myEvent.userID}
          onClose={() => setLogbookDropdownOpen(false)}
          isOpen={logbookDropdownOpen}
          language={language}
          initialArt={art}
        />
      )}
      {isArtEventDropdownOpen && (
        <ArtEventDropdown
          eventId={myEvent.id} // Pass myEvent.id explicitly
          onClose={() => setIsArtEventDropdownOpen(false)} // Close dropdown handler
          language={language}
          eventType="artEvent"
          initialArtEvent={artEvent}
          art={art}
          // Keep the first-row tag pill in sync with edits made inside
          // the overlay — without this, the overlay updates only its
          // local state and the outer row (and next overlay open, which
          // re-seeds from initialArtEvent) shows the stale tag.
          onEventUpdated={(updatedEvent) => {
            setMyEvent(updatedEvent);
            setArtEvent((prev) => (prev ? { ...prev, event: updatedEvent } : prev));
          }}
        />
      )}
    </div>
  );
};

// Inline one-tap date prompt rendered right under the tag pill on the
// art page. Reduces the "tag → navigate → scroll → open drawer → pick
// date → confirm" journey to a single click for the common case.
const QuickDatePrompt = ({ artId, language, posting, setPosting, onOpenFull, onPosted, isMovie, category }) => {
  // Verb-specific label per category. Games have no natural inflection
  // for "watch/read", so the game label stays neutral.
  const label = language === 'ru'
    ? category === 1 ? 'Отметить дату просмотра в журнале:'
      : category === 3 ? 'Отметить дату чтения в журнале:'
      : 'Отметить дату в журнале:'
    : category === 1 ? 'Mark date watched in the logbook:'
      : category === 3 ? 'Mark date read in the logbook:'
      : 'Mark date in the logbook:';
  const now = language === 'ru' ? 'Сейчас' : 'Now';
  const yesterday = language === 'ru' ? 'Вчера' : 'Yesterday';
  const other = language === 'ru' ? 'Другая дата…' : 'Other date…';
  const postDate = (dt) => {
    if (posting) return;
    setPosting(true);
    axiosInstance
      .post(`/art/logbook/${artId}/add`, { date: dt.toISOString(), note: '', finished: isMovie })
      .then(() => { setPosting(false); onPosted(); })
      .catch((e) => { setPosting(false); console.error('logbook quick-add failed', e); });
  };
  const nowDate = () => new Date();
  const yesterdayDate = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d; };
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-sm" style={{ opacity: posting ? 0.5 : 1 }}>
      <span style={{ color: 'var(--mantine-color-dimmed)' }}>{label}</span>
      <button type="button" className="underline-offset-2 hover:underline" style={{ color: 'var(--mantine-color-blue-text)' }} onClick={() => postDate(nowDate())} disabled={posting}>{now}</button>
      <button type="button" className="underline-offset-2 hover:underline" style={{ color: 'var(--mantine-color-blue-text)' }} onClick={() => postDate(yesterdayDate())} disabled={posting}>{yesterday}</button>
      <button type="button" className="underline-offset-2 hover:underline" style={{ color: 'var(--mantine-color-blue-text)' }} onClick={onOpenFull} disabled={posting}>{other}</button>
    </div>
  );
};

export default CardEventArtChatMini;